import type { GQL, Model } from "@binale-tech/shared";

import { Category } from "@domain/models/Category";
import { Creditor, Debitor } from "@domain/models/Creditor";
import React, {
    createContext,
    use,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import { Tag } from "@domain/models/Tag";
import { CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import {
    categoryDelete,
    categorySave,
    creditorCreate,
    creditorDelete,
    creditorUpdate,
    debitorCreate,
    debitorDelete,
    debitorUpdate,
    genericRecordTextTemplateSave,
    tagCreate,
    tagDelete,
    tagUpdate,
} from "@domain/context/accountingData/mutations/kontoMutations.graphql";
import {
    refCategory,
    refCreditor,
    refDebitor,
    refRecordTextTemplates,
    refTag,
} from "@domain/api/firebase/firebaseRootRefs";
import { useMutation } from "@apollo/client/react";
import { type DataSnapshot, child } from "firebase/database";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { useGroupedCollectionListener } from "@domain/context/hooks/useGroupedCollectionListener";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

export type AccountMaps = {
    categoryOverridesMap: Map<string, Model.INumName>;
    creditorsMap: Map<string, Creditor>;
    debitorsMap: Map<string, Debitor>;
    tagsMap: Map<string, Tag>;
};
type KontoCtxData = {
    categoryOverrides: Model.INumName[];
    categoryOverridesAllMap: Map<string, Model.INumName[]>;
    creditors: Creditor[];
    creditorsAllMap: Map<string, Creditor[]>;
    debitors: Debitor[];
    debitorsAllMap: Map<string, Creditor[]>;
    tags: Tag[];
    tagsAllMap: Map<string, Tag[]>;
    accountMapsAllYears: Map<number, AccountMaps>;
    recordTextTemplates: GQL.IGenericRecordTextTemplate[];
    isLoaded?: boolean;
};
const getDefaultAccountsMap = (): AccountMaps => ({
    categoryOverridesMap: new Map(),
    creditorsMap: new Map(),
    debitorsMap: new Map(),
    tagsMap: new Map(),
});
const getDefaultValue = (): KontoCtxData => ({
    categoryOverrides: [],
    categoryOverridesAllMap: new Map(),
    creditors: [],
    creditorsAllMap: new Map(),
    debitors: [],
    debitorsAllMap: new Map(),
    tags: [],
    tagsAllMap: new Map(),
    accountMapsAllYears: new Map(),
    recordTextTemplates: [],
    isLoaded: false,
});
export const KontoContext = createContext<KontoCtxData>(getDefaultValue());

type KontoController = {
    categoryOverrideSave: (v: Model.INumName) => Promise<void>;
    categoryOverrideDelete: (id: string, num: string) => Promise<void>;
    creditorSave: (v: Creditor) => Promise<void>;
    creditorDelete: (id: string, num: string) => Promise<void>;
    debSave: (v: Debitor) => Promise<void>;
    debDelete: (id: string, num: string) => Promise<void>;
    tagSave: (v: Tag) => Promise<void>;
    tagDelete: (id: string, num: string) => Promise<void>;
    recordTextTemplateSave: (v: Omit<GQL.IGenericRecordTextTemplate, "companyId">) => Promise<void>;
};
export const KontoControlContext = createContext<KontoController>({
    categoryOverrideSave: () => Promise.resolve(),
    categoryOverrideDelete: () => Promise.resolve(),
    creditorSave: () => Promise.resolve(),
    creditorDelete: () => Promise.resolve(),
    debSave: () => Promise.resolve(),
    debDelete: () => Promise.resolve(),
    tagSave: () => Promise.resolve(),
    tagDelete: () => Promise.resolve(),
    recordTextTemplateSave: () => Promise.resolve(),
});

const toMap = <T extends Model.INumName>(vs: T[]): Map<string, T> => {
    const map = new Map<string, T>();
    vs.forEach((v) => {
        map.set(v.num, v);
    });
    return map;
};

export const EntitiesProvider: FC<PropsWithChildren> = (props) => {
    const { companyGQL, isLoaded: isCompanyLoaded } = use(CompanyContext);
    const companyId = companyGQL?.id;
    const { year } = use(YearPeriodContext);
    const toastSaver = useToastSaver();

    const [categorySaveMutation] = useMutation(categorySave);
    const [categoryDeleteMutation] = useMutation(categoryDelete);
    const [creditorCreateMutation] = useMutation(creditorCreate);
    const [creditorUpdateMutation] = useMutation(creditorUpdate);
    const [creditorDeleteMutation] = useMutation(creditorDelete);
    const [debitorCreateMutation] = useMutation(debitorCreate);
    const [debitorUpdateMutation] = useMutation(debitorUpdate);
    const [debitorDeleteMutation] = useMutation(debitorDelete);
    const [tagCreateMutation] = useMutation(tagCreate);
    const [tagUpdateMutation] = useMutation(tagUpdate);
    const [tagDeleteMutation] = useMutation(tagDelete);
    const [recordTextTemplateSaveMutation] = useMutation(genericRecordTextTemplateSave);

    const [value, setValue] = useState<KontoCtxData>(getDefaultValue());

    const shouldSkipLoad = useMemo(() => !ProductAccessUtils.hasCompanyAccountingAccess(companyGQL), [companyGQL]);

    const years = useMemo(
        () => (companyGQL?.accountingConfigs ?? []).map((c) => ({ groupId: String(c.year), subPath: String(c.year) })),
        [companyGQL?.accountingConfigs],
    );

    const categoriesBaseRef = useMemo(() => child(refCategory, `${companyId}`), [companyId]);
    const categoriesInitializer = useCallback((snap: DataSnapshot): Category => {
        const { num, name, marked } = snap.val();
        return Category.unserialize({ num, name, marked, key: snap.key });
    }, []);
    const { state: categoriesGrouped, isLoaded: isLoadedCat } = useGroupedCollectionListener(
        categoriesBaseRef,
        years,
        categoriesInitializer,
        shouldSkipLoad,
    );

    const debitorsBaseRef = useMemo(() => child(refDebitor, `${companyId}`), [companyId]);
    const debitorsInitializer = useCallback((snap: DataSnapshot): Debitor => Debitor.unserialize(snap.val()), []);
    const { state: debitorsGrouped, isLoaded: isLoadedDeb } = useGroupedCollectionListener(
        debitorsBaseRef,
        years,
        debitorsInitializer,
        shouldSkipLoad,
    );

    const creditorsBaseRef = useMemo(() => child(refCreditor, `${companyId}`), [companyId]);
    const creditorsInitializer = useCallback((snap: DataSnapshot): Creditor => Creditor.unserialize(snap.val()), []);
    const { state: creditorsGrouped, isLoaded: isLoadedCred } = useGroupedCollectionListener(
        creditorsBaseRef,
        years,
        creditorsInitializer,
        shouldSkipLoad,
    );

    const tagsBaseRef = useMemo(() => child(refTag, `${companyId}`), [companyId]);
    const tagsInitializer = useCallback((snap: DataSnapshot): Tag => Tag.unserialize(snap.val()), []);
    const { state: tagsGrouped, isLoaded: isLoadedTags } = useGroupedCollectionListener(
        tagsBaseRef,
        years,
        tagsInitializer,
        shouldSkipLoad,
    );

    const textTemplatesRef = useMemo(() => child(refRecordTextTemplates, `${companyId}`), [companyId]);
    const textTemplatesInitializer = useCallback((snap: DataSnapshot) => {
        return snap.val() as GQL.IGenericRecordTextTemplate;
    }, []);
    const { list: recordTextTemplates } = useCollectionListener<GQL.IGenericRecordTextTemplate>(
        textTemplatesRef,
        textTemplatesInitializer,
        shouldSkipLoad,
    );

    const aggregatedValue: KontoCtxData = useMemo(() => {
        const toSortedArray = <T extends Model.INumName>(itemsMap: Map<string, T> | undefined): T[] =>
            Array.from(itemsMap?.values() ?? []).toSorted((a, b) => Number(a.num) - Number(b.num));

        // Build categoryOverridesAllMap applying fixNum per year
        const categoryOverridesAllMap = new Map<string, Model.INumName[]>();
        for (const [yearStr, itemsMap] of categoriesGrouped.entries()) {
            const kontoExt = companyGQL?.accountingConfigs?.find((c) => c.year === Number(yearStr))?.kontoExt ?? 0;
            categoryOverridesAllMap.set(
                yearStr,
                toSortedArray(itemsMap).map((c) => c.fixNum(kontoExt)),
            );
        }

        const creditorsAllMap = new Map<string, Creditor[]>();
        for (const [yearStr, itemsMap] of creditorsGrouped.entries()) {
            creditorsAllMap.set(yearStr, toSortedArray(itemsMap));
        }

        const debitorsAllMap = new Map<string, Debitor[]>();
        for (const [yearStr, itemsMap] of debitorsGrouped.entries()) {
            debitorsAllMap.set(yearStr, toSortedArray(itemsMap));
        }

        const tagsAllMap = new Map<string, Tag[]>();
        for (const [yearStr, itemsMap] of tagsGrouped.entries()) {
            tagsAllMap.set(yearStr, toSortedArray(itemsMap));
        }

        const accountMapsAllYears = new Map<number, AccountMaps>();
        (companyGQL?.accountingConfigs ?? []).forEach((c) => accountMapsAllYears.set(c.year, getDefaultAccountsMap()));

        if (accountMapsAllYears.size) {
            for (const [yearStr, cats] of categoryOverridesAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).categoryOverridesMap = toMap(cats);
                }
            }
            for (const [yearStr, creds] of creditorsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).creditorsMap = toMap(creds);
                }
            }
            for (const [yearStr, debs] of debitorsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).debitorsMap = toMap(debs);
                }
            }
            for (const [yearStr, tags] of tagsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).tagsMap = toMap(tags);
                }
            }
        }

        return {
            categoryOverrides: categoryOverridesAllMap.get(String(year)) ?? [],
            creditors: creditorsAllMap.get(String(year)) ?? [],
            debitors: debitorsAllMap.get(String(year)) ?? [],
            tags: tagsAllMap.get(String(year)) ?? [],
            categoryOverridesAllMap,
            creditorsAllMap,
            debitorsAllMap,
            tagsAllMap,
            accountMapsAllYears,
            recordTextTemplates,
            isLoaded: isLoadedCat && isLoadedCred && isLoadedDeb && isLoadedTags && isCompanyLoaded,
        };
    }, [
        categoriesGrouped,
        creditorsGrouped,
        debitorsGrouped,
        tagsGrouped,
        year,
        companyGQL?.accountingConfigs,
        recordTextTemplates,
        isLoadedCat,
        isLoadedCred,
        isLoadedDeb,
        isLoadedTags,
        isCompanyLoaded,
    ]);

    const deferredValue = useDeferredValue(aggregatedValue);
    useEffect(() => {
        if (deferredValue.isLoaded) {
            setValue(deferredValue);
        }
        return () => {
            setValue(getDefaultValue());
        };
    }, [deferredValue]);

    const kontoControl: KontoController = useMemo(
        () => ({
            categoryOverrideSave: async (category) => {
                await toastSaver(() =>
                    categorySaveMutation({
                        variables: {
                            input: {
                                companyId,
                                name: category.name,
                                accountNumString: category.num,
                                year,
                            },
                        },
                    }),
                );
            },
            categoryOverrideDelete: async (id, num) => {
                await toastSaver(() =>
                    categoryDeleteMutation({
                        variables: {
                            input: {
                                companyId,
                                id,
                                year,
                                num,
                            },
                        },
                    }),
                );
            },
            creditorSave: async (creditor) => {
                const { name, description } = creditor;
                const data = {
                    name,
                    description,
                    companyId,
                };
                if (creditor.key) {
                    await toastSaver(() =>
                        creditorUpdateMutation({
                            variables: {
                                input: {
                                    ...data,
                                    id: creditor.key,
                                    num: creditor.num,
                                    year,
                                },
                            },
                        }),
                    );
                } else {
                    await toastSaver(() =>
                        creditorCreateMutation({
                            variables: {
                                input: {
                                    ...data,
                                    year,
                                    accountNum: +creditor.num,
                                },
                            },
                        }),
                    );
                }
            },
            creditorDelete: async (id, num) => {
                await toastSaver(() =>
                    creditorDeleteMutation({
                        variables: {
                            input: {
                                companyId,
                                id,
                                year,
                                num,
                            },
                        },
                    }),
                );
            },
            debSave: async (debitor) => {
                const { name, description } = debitor;
                const data = {
                    name,
                    description,
                    companyId,
                };
                if (debitor.key) {
                    await toastSaver(() =>
                        debitorUpdateMutation({
                            variables: {
                                input: {
                                    ...data,
                                    id: debitor.key,
                                    num: debitor.num,
                                    year,
                                },
                            },
                        }),
                    );
                } else {
                    await toastSaver(() =>
                        debitorCreateMutation({
                            variables: {
                                input: {
                                    ...data,
                                    accountNum: +debitor.num,
                                    year,
                                },
                            },
                        }),
                    );
                }
            },
            debDelete: async (id, num) => {
                await toastSaver(() =>
                    debitorDeleteMutation({
                        variables: {
                            input: {
                                companyId,
                                id,
                                year,
                                num,
                            },
                        },
                    }),
                );
            },
            tagSave: async (tag) => {
                if (tag.key) {
                    await toastSaver(() =>
                        tagUpdateMutation({
                            variables: {
                                input: {
                                    companyId,
                                    id: tag.key,
                                    num: tag.num,
                                    name: tag.name,
                                    year,
                                },
                            },
                        }),
                    );
                } else {
                    await toastSaver(() =>
                        tagCreateMutation({
                            variables: {
                                input: {
                                    companyId,
                                    name: tag.name,
                                    num: tag.num,
                                    year,
                                },
                            },
                        }),
                    );
                }
            },
            tagDelete: async (id, num) => {
                await toastSaver(() =>
                    tagDeleteMutation({
                        variables: {
                            input: {
                                companyId,
                                id,
                                year,
                                num,
                            },
                        },
                    }),
                );
            },
            recordTextTemplateSave: async (v) => {
                await toastSaver(() =>
                    recordTextTemplateSaveMutation({
                        variables: {
                            input: {
                                companyId,
                                ...v,
                                bankIds: v.bankIds ?? [],
                                kbIds: v.kbIds ?? [],
                            },
                        },
                    }),
                );
            },
        }),
        [
            categoryDeleteMutation,
            categorySaveMutation,
            companyId,
            creditorCreateMutation,
            creditorDeleteMutation,
            creditorUpdateMutation,
            debitorCreateMutation,
            debitorDeleteMutation,
            debitorUpdateMutation,
            recordTextTemplateSaveMutation,
            tagCreateMutation,
            tagDeleteMutation,
            tagUpdateMutation,
            toastSaver,
            year,
        ],
    );
    return (
        <KontoContext value={value}>
            <KontoControlContext value={kontoControl}>{props.children}</KontoControlContext>
        </KontoContext>
    );
};
